import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { BadgeCheck, Loader2, ArrowRight, FileText, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * NetworkAgreementPage — 1-click Digital Marketing Performance Agreement.
 * Shown to agents after they click "Claim & Sign Agreement" in the lead
 * alert email or from the Agent Dashboard.
 */
const NetworkAgreementPage = () => {
  const [info, setInfo] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/network/agreement`, token ? {
      headers: { Authorization: `Bearer ${token}` },
    } : {}).then((r) => {
      setInfo(r.data);
      if (r.data?.signed) setSigned(r.data.signed);
    }).catch(() => null).finally(() => setLoading(false));
  }, []);

  const handleSign = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login?next=/network/agreement';
      return;
    }
    setSigning(true);
    try {
      const r = await axios.post(`${API_URL}/api/network/agreement/sign`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSigned({
        signed_at: r.data.signed_at,
        version: r.data.version,
        referral_fee_pct: r.data.referral_fee_pct,
      });
      toast.success(r.data.already_signed ? 'Already signed — you are an active Partner.' : 'Agreement signed. Welcome to the Network.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signing failed');
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="network-agreement-page">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <span className="text-[10px] tracking-[0.4em] text-[#E07A3A] uppercase font-semibold">Shopperden Agent-Network Bridge</span>
        <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-tight mb-4">
          Digital Marketing Performance Agreement
        </h1>
        <p className="text-slate-600 text-base max-w-2xl mb-8">
          One-click acceptance of Shopperden's AI-Network referral terms.
          {info?.referral_fee_pct ? ` ${info.referral_fee_pct}% AI Marketing Performance Fee, payable on closure only.` : ''}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <div className="luxury-card p-5">
            <ShieldCheck className="h-5 w-5 text-[#1F3D2B] mb-2" />
            <h3 className="font-medium text-sm">Only pay on closure</h3>
            <p className="text-xs text-slate-500 mt-1">No fees until a deal closes.</p>
          </div>
          <div className="luxury-card p-5">
            <FileText className="h-5 w-5 text-[#1F3D2B] mb-2" />
            <h3 className="font-medium text-sm">30-day termination</h3>
            <p className="text-xs text-slate-500 mt-1">Leave anytime with 30 days' notice.</p>
          </div>
          <div className="luxury-card p-5">
            <BadgeCheck className="h-5 w-5 text-[#E07A3A] mb-2" />
            <h3 className="font-medium text-sm">Auto-verified listings</h3>
            <p className="text-xs text-slate-500 mt-1">£29/mo Partner tier optional after signing.</p>
          </div>
        </div>

        <div className="luxury-card p-6 sm:p-8 bg-white border border-slate-200">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading agreement…</div>
          ) : (
            <>
              <pre className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed font-sans max-h-96 overflow-y-auto bg-slate-50 border border-slate-200 p-5 rounded mb-6" data-testid="dmpa-terms">
                {info?.terms_md}
              </pre>

              {signed ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-5 text-emerald-800" data-testid="dmpa-signed">
                  <BadgeCheck className="h-6 w-6 text-emerald-600 mb-2" />
                  <h4 className="font-semibold">Agreement signed</h4>
                  <p className="text-sm mt-1">
                    Version <strong>{signed.version}</strong> · signed {new Date(signed.signed_at).toLocaleString()}
                  </p>
                  <Link to="/agent" className="inline-flex items-center gap-2 mt-3 text-sm text-[#E07A3A] hover:text-[#C4652E] font-semibold">
                    Go to Partner Dashboard <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                  <Button
                    onClick={handleSign}
                    disabled={signing}
                    className="bg-[#E07A3A] text-white hover:bg-[#C4652E] tracking-widest text-sm px-7 py-5 font-bold uppercase"
                    data-testid="dmpa-sign-btn"
                  >
                    {signing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BadgeCheck className="h-4 w-4 mr-2" />}
                    1-Click Accept & Join Network
                  </Button>
                  <p className="text-[11px] text-slate-500">
                    By clicking, you accept the {info?.referral_fee_pct || 20}% AI Marketing Performance Fee on successful closures.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetworkAgreementPage;
